import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { RecordPicker } from '../../components/RecordPicker.js';
import { personLabel } from '../../components/ApprovalLevels.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

interface DeveloperRow {
  readonly developerId: string;
  readonly userId: string;
  /** The developer, named by the server (G18). */
  readonly actorName: string | null;
  readonly actorCode: string | null;
  readonly developerRole: string;
  readonly sandboxQuota: number;
  readonly appsCreated: number;
  readonly status: string;
}

const ROLES = ['', 'CREATOR', 'REVIEWER', 'APPROVER', 'PUBLISHER', 'ADMINISTRATOR'];
const STATUSES = ['', 'ACTIVE', 'SUSPENDED', 'INACTIVE'];
const ENROL_ROLES = ROLES.filter(Boolean);

/**
 * M28 — Governance Dashboard (§23 / §32). The citizen-developer governance
 * register underpinning the maker-checker model (BR-01). Lists every enrolled
 * citizen_developer (T376) with their governance role (creator, reviewer,
 * approver, publisher, administrator), sandbox quota, applications created and
 * enrolment status. Supports enrolling a new developer and suspending /
 * reactivating an existing one, each wired to the guarded
 * /v1/lcnc/governance/developers write API. Filterable by developer role and
 * status. Segregation of duties across these roles enforces the maker ≠ checker
 * publish/deploy gates.
 */
export function M28GovernanceDashboardPage(): ReactNode {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [rows, setRows] = useState<DeveloperRow[]>([]);
  const [developerRole, setDeveloperRole] = useState('');
  const [status, setStatus] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);

  const [userId, setUserId] = useState('');
  const [userLabel, setUserLabel] = useState('');
  const [newRole, setNewRole] = useState('CREATOR');
  const [quota, setQuota] = useState('5');
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (developerRole) params.set('developerRole', developerRole);
    if (status) params.set('status', status);
    const qs = params.toString();
    apiRequest<{ data: DeveloperRow[] }>(`/v1/lcnc/governance/developers${qs ? `?${qs}` : ''}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, developerRole, status, reload]);

  const failMessage = (err: unknown): string =>
    err instanceof ApiError && err.status === 403
      ? t('m28.governance.notAuthorised')
      : (err as Error).message;

  const enroll = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setActionError(null);
    setNotice(null);
    if (!userId.trim()) { setActionError(t('m28.governance.validationUserId')); return; }
    setBusy(true);
    try {
      await apiRequest(`/v1/lcnc/governance/developers`, {
        method: 'POST',
        body: { userId: userId.trim(), developerRole: newRole, sandboxQuota: Number(quota) || 0 },
        token,
      });
      setNotice(t('m28.governance.noticeEnrolled', { user: userLabel }));
      setUserId('');
      setUserLabel('');
      setReload((n) => n + 1);
    } catch (err) {
      setActionError(failMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const setDeveloperStatus = async (d: DeveloperRow, next: string): Promise<void> => {
    setActionError(null);
    setNotice(null);
    try {
      await apiRequest(`/v1/lcnc/governance/developers/${d.developerId}/status`, {
        method: 'POST', body: { status: next }, token,
      });
      setNotice(t('m28.governance.noticeStatus', { user: personLabel(d.actorName, d.actorCode) ?? '—', status: next }));
      setReload((n) => n + 1);
    } catch (err) {
      setActionError(failMessage(err));
    }
  };

  return (
    <main aria-label="governance-dashboard">
      <h1>{t('m28.governance.title')}</h1>

      <section aria-label="enroll-developer">
        <h2>{t('m28.governance.enrollHeading')}</h2>
        <form aria-label="developer-form" onSubmit={(e) => void enroll(e)}>
          <RecordPicker<{ userId: string; displayName: string; employeeCode: string | null }>
            endpoint="/v1/users?limit=200"
            idField="userId"
            label={t('m28.governance.fieldUserId')}
            testId="field-user-id"
            value={userId}
            optionLabel={(u) => personLabel(u.displayName, u.employeeCode) ?? u.displayName}
            onPick={(id, label) => { setUserId(id); setUserLabel(label); }}
          />
          <label>{t('m28.governance.fieldRole')}
            <select aria-label="field-developer-role" value={newRole} onChange={(e) => setNewRole(e.target.value)}>
              {ENROL_ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </label>
          <label>{t('m28.governance.fieldQuota')}<input aria-label="field-sandbox-quota" type="number" value={quota} onChange={(e) => setQuota(e.target.value)} /></label>
          <button type="submit" aria-label="submit-developer" disabled={busy}>{busy ? t('m28.governance.enrolling') : t('m28.governance.enroll')}</button>
        </form>
        {actionError && <p role="alert" aria-label="developer-error">{actionError}</p>}
        {notice && <p aria-label="developer-notice">{notice}</p>}
      </section>

      <div className="filters">
        <label>
          {t('m28.governance.filterRole')}
          <select aria-label="filter-developer-role" value={developerRole} onChange={(e) => setDeveloperRole(e.target.value)}>
            {ROLES.map((r) => <option key={r || 'all'} value={r}>{r || t('m28.governance.all')}</option>)}
          </select>
        </label>
        <label>
          {t('m28.governance.filterStatus')}
          <select aria-label="filter-status" value={status} onChange={(e) => setStatus(e.target.value)}>
            {STATUSES.map((s) => <option key={s || 'all'} value={s}>{s || t('m28.governance.all')}</option>)}
          </select>
        </label>
      </div>
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{t('m28.governance.loading')}</p> : (
        <>
          <p aria-label="developer-count">{t('m28.governance.count', { count: rows.length })}</p>
          <table aria-label="developer-table">
            <thead><tr><th>{t('m28.governance.colUser')}</th><th>{t('m28.governance.colRole')}</th><th>{t('m28.governance.colQuota')}</th><th>{t('m28.governance.colAppsCreated')}</th><th>{t('m28.governance.colStatus')}</th><th>{t('m28.governance.colActions')}</th></tr></thead>
            <tbody>
              {rows.map((d) => (
                <tr key={d.developerId}>
                  <td>{personLabel(d.actorName, d.actorCode) ?? '—'}</td><td>{d.developerRole}</td><td>{d.sandboxQuota}</td>
                  <td>{d.appsCreated}</td><td>{d.status}</td>
                  <td>
                    {d.status === 'SUSPENDED'
                      ? <button type="button" aria-label={`activate-${d.userId}`} onClick={() => void setDeveloperStatus(d, 'ACTIVE')}>{t('m28.governance.btnActivate')}</button>
                      : <button type="button" aria-label={`suspend-${d.userId}`} onClick={() => void setDeveloperStatus(d, 'SUSPENDED')}>{t('m28.governance.btnSuspend')}</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </main>
  );
}
